/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState } from 'react';
import { Badge } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import TruncateText from '../../shared/TruncatedText/TruncateText';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import { IDataTableColumn } from '../MeterSavingsDetailTable/IMeterSavingsDetailTableProps';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import CommonProgressBar from '../../shared/CommonProgressBar/CommonProgressBar';
import './CarbonDetailTabBreakdownByMeterTheme.scss';
import GasIcon from '../../../images/GasIcon.svg';
import ElectricityIcon from '../../../images/Energy-icon.svg';
import {
  CarbonDetailExpandMeterField,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  KPI_VALUE_DECIMAL_PRECISION,
  UtilityType
} from '../Utils/Constants';
import { getAbsoluteValue } from '../Utils/Utils';
import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { IDetailBreakdownByMeter } from '../../DetailKPIWidgets/EnergyUsagesViewDetailsWidget/AllUtilitiesDetails/IEnergyUsagesBreakdownProps';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { expanderTemplate } from '../MeterSavingsDetailTable/MeterSavingsDetailTableUtility';
import { ConversionType } from '../Utils/unitHandling/UnitModel';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';

const DetailBreakdownByMeter = (props: IDetailBreakdownByMeter) => {
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    CarbonDetailExpandMeterField.actualConsumption
  );
  const { meterData, utilityMeasurementUnit, columnDetails } = props;
  const [expandedRows, setexpandedRows] = useState([meterData[0]]);
  const e = descriptors.DetailedView;
  const [t] = useTranslation();

  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;

    if (field === CarbonDetailExpandMeterField.actualConsumption) {
      return (
        <span className="score-category-meterdata">
          <span className="score-category-percent">
            <div className="measurementunit">
              {
                getConvertedValue(
                  ConversionType.CARBON,
                  props.emissionMeasurementUnit
                    ? props.emissionMeasurementUnit
                    : '',
                  value,
                  rowData.actualUnit
                )?.Value
              }
            </div>
            {rowData.actualUnit}
          </span>
          <span className="score-category-progress">
            <CommonProgressBar
              actual={rowData.totalActualEmission}
              previousPeriod={rowData.totalPreviousPeriodEmission}
              savings={rowData.totalDeviation}
              savingsPercentage={rowData.percentageEmissionsSaving}
              progessBarRelativeWidth={rowData.progessBarRelativeWidth}
            />
          </span>
        </span>
      );
    }
    if (field === CarbonDetailExpandMeterField.utilityType) {
      return (
        <span className="score-category-meterdata">
          <span className="score-category-percent">
            <div className="utilityType">{rowData.utilityType}</div>
          </span>
        </span>
      );
    }

    if (field === CarbonDetailExpandMeterField.change) {
      const convertedValue: IAny = getConvertedValue(
        ConversionType.CARBON,
        props.emissionMeasurementUnit ? props.emissionMeasurementUnit : '',
        value,
        rowData.deviationUnit
      )?.Value;
      return value === null ? (
        <>--</>
      ) : (
        <span className="score-category-meterdata-meter">
          <span
            className="savings-badge"
            style={{
              color: getPercentageColor(convertedValue)
            }}
          >
            {getAbsoluteValue(convertedValue)}
            {' ' +
              rowData.deviationUnit +
              '(' +
              getAbsoluteValue(rowData.percentageEmissionsSaving) +
              '%)'}
          </span>
        </span>
      );
    }
    const badgeCheck = (rowVal: IAny) =>
      rowVal.totalDeviation < 0 && <Badge color="red" empty />;
    if (field === CarbonDetailExpandMeterField.meter) {
      if (
        rowData.utilityType.toLowerCase() ===
          UtilityType.Electricity.toLowerCase() ||
        rowData.utilityType.toLowerCase() === UtilityType.Electric
      ) {
        return (
          <>
            <span className="score-category-meterdata-meter ">
              {badgeCheck(rowData)}
              <span className="score-category-meterparent"> {t(e.SCOPE2)}</span>
            </span>
          </>
        );
      }
      if (rowData.utilityType.toLowerCase() === UtilityType.Gas.toLowerCase()) {
        return (
          <>
            <span className="score-category-meterdata-meter ">
              {badgeCheck(rowData)}
              <span className="score-category-meterparent"> {t(e.SCOPE1)}</span>
            </span>
          </>
        );
      }
    }
    if (field === CarbonDetailExpandMeterField.baselineConsumption) {
      const convertedValue: IAny = getConvertedValue(
        ConversionType.CARBON,
        props.emissionMeasurementUnit ? props.emissionMeasurementUnit : '',
        value,
        rowData.baselineUnit
      )?.Value;
      return (
        <span className="score-category-meterdata-meter">
          <div className="consumptionvalue">{convertedValue}</div>
          {rowData.baselineUnit}
        </span>
      );
    }
    return <></>;
  };

  const rowcellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;

    if (field === CarbonDetailExpandMeterField.actualConsumption) {
      return (
        <span className="emaas-carbondetail-category-meterdata">
          <span className="score-category-percent">
            <div className="measurementunit">{value}</div>
            {utilityMeasurementUnit}
          </span>
          <span className="score-category-progress">
            <CommonProgressBar
              actual={rowData.totalActualEmission}
              previousPeriod={rowData.totalPreviousPeriodEmission}
              savings={rowData.totalDeviation}
              savingsPercentage={rowData.totalDeviationPercentage}
              progessBarRelativeWidth={rowData.progessBarRelativeWidth}
            />
          </span>
        </span>
      );
    }

    if (field === CarbonDetailExpandMeterField.meter) {
      if (
        rowData.utilityType.toLowerCase() ===
          UtilityType.Electricity.toLowerCase() ||
        rowData.utilityType.toLowerCase() === UtilityType.Electric
      ) {
        return (
          <span className="score-category-meterdata-meter ">
            <span className="score-category-Badge">
              <span className="score-category-percent">
                <div className="measurementunit">
                  <img
                    className="card-electricity-icon"
                    src={ElectricityIcon}
                  />
                  <TruncateText text={rowData.meterName} />
                </div>
              </span>
            </span>
          </span>
        );
      }
      if (rowData.utilityType.toLowerCase() === UtilityType.Gas.toLowerCase()) {
        return (
          <span className="score-category-meterdata-meter ">
            <span className="score-category-Badge">
              <span className="score-category-percent">
                <div className="measurementunit ">
                  <img className="card-electricity-icon" src={GasIcon} />
                  <TruncateText text={rowData.meterName} />
                </div>
              </span>
            </span>
          </span>
        );
      }
    }

    if (field === CarbonDetailExpandMeterField.baselineConsumption) {
      return (
        <span className="score-category-meterdata-meter">
          {value} {utilityMeasurementUnit}
        </span>
      );
    }
    if (field === CarbonDetailExpandMeterField.change) {
      return value === null ? (
        <>--</>
      ) : (
        <span className="score-category-meterdata-meter">
          <span
            className="savings-badge"
            style={{
              color: getPercentageColor(value)
            }}
          >
            {getAbsoluteValue(value).toFixed(KPI_VALUE_DECIMAL_PRECISION)}
            {' ' +
              utilityMeasurementUnit +
              '(' +
              getAbsoluteValue(
                rowData.totalDeviationPercentage.toFixed(
                  KPI_PERCENTAGE_DECIMAL_PRECISION
                )
              ) +
              '%)'}
          </span>
        </span>
      );
    }
    if (field === CarbonDetailExpandMeterField.utilityType) {
      return (
        <span className="score-category-meterdata">
          <span className="score-category-percent">
            <div className="utilityType">{rowData.utilityType}</div>
          </span>
        </span>
      );
    }
    return <></>;
  };

  const rowExpansionTemplate = (data: IAny) => {
    return (
      <div className="carbondetail-row-expansion">
        <DataTable data={data.utilityMeters} showHeader={false}>
          <DataTable.Column initialWidth="1.5%" field="" header="" />
          {columnDetails.map((column: IDataTableColumn) => {
            return (
              <DataTable.Column
                className={column.className}
                field={column.field}
                header={column.header}
                sortable={column.sortable}
                renderer={rowcellDataRenderer}
                initialWidth={column.initialWidth}
              />
            );
          })}
        </DataTable>
      </div>
    );
  };

  return (
    <div className="Savings-Datatable">
      <DataTable
        className="nonflat-table Savingtable"
        expandedRows={expandedRows}
        rowExpansionTemplate={rowExpansionTemplate}
        data={meterData}
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
        }}
        sortField={sortFieldName}
        sortOrder={fieldSortOrder}
        scrollable={true}
        scrollHeight="20rem"
      >
        <DataTable.Column
          initialWidth="1.5%"
          field=""
          header=""
          renderer={(data: ICellData) =>
            expanderTemplate(data, expandedRows, setexpandedRows)
          }
        />
        {columnDetails.map((column: IDataTableColumn) => {
          return (
            <DataTable.Column
              className={column.className}
              field={column.field}
              header={column.header}
              sortable={column.sortable}
              renderer={cellDataRenderer}
              initialWidth={column.initialWidth}
            />
          );
        })}
      </DataTable>
    </div>
  );
};
export default DetailBreakdownByMeter;
